import { DateTime } from 'luxon'
const CARE_NURSE_ROLE_ID = 5204

export const useChatStore = defineStore('chatStore', () => {
  const channels = ref<Channel[]>([])
  const messages = ref<Message[]>([])
  const selectedChannel = ref<Channel | null>(null)
  const messagesContainer = ref<HTMLElement | null>(null)
  const userStore = useUserStore()
  const participantsByChannelId = ref<Map<number, (User | Contact)[]>>(new Map())
  const chatEndpoint = computed(() => (userStore.isLoggedInAsContact ? 'css/chat' : 'chat'))
  const usersEndpoint = computed(() => (userStore.isLoggedInAsContact ? 'css/users' : 'users'))
  const translateEndpoint = computed(() =>
    userStore.isLoggedInAsContact ? 'css/translate' : 'translate'
  )
  const contactsEndpoint = computed(() =>
    userStore.isLoggedInAsContact ? 'css/contacts' : 'contacts'
  )
  const contactOrUser = computed(() => (userStore.isLoggedInAsContact ? 'contact' : 'user'))
  const unreadMessages = ref<Map<number, Message[]>>(new Map())
  const bkGroupsKey = ref(0)
  const isLoadingChannels = ref(false)
  const currentLanguage = ref('')

  const currentPage = ref(1)
  const hasMoreMessages = ref(true)
  const isLoadingMoreMessages = ref(false)
  const MESSAGES_PER_PAGE = 11

  function checkIsUser(participant: User | Contact): participant is User {
    return 'role_id' in participant
  }

  const scrollToBottom = () => {
    nextTick(() => {
      if (messagesContainer.value) {
        messagesContainer.value.scrollTop = messagesContainer.value.scrollHeight
      }
    })
  }

  const loadMessages = async (channelId: number, resetPagination = true) => {
    if (resetPagination) {
      currentPage.value = 1
      hasMoreMessages.value = true
      messages.value = []
    }

    const response = await useApi(
      `${chatEndpoint.value}/messages?filter[channel_id]=${channelId}&per_page=${MESSAGES_PER_PAGE}&page=${currentPage.value}&sort=-created_at&include=messageTranslations`,
      'GET'
    )

    const newMessages = (await translateMessages(response.data)) || []

    if (newMessages.length < MESSAGES_PER_PAGE) {
      hasMoreMessages.value = false
    }

    newMessages.reverse()

    newMessages
      .filter((message: Message) =>
        userStore.isLoggedInAsContact
          ? userStore.contact?.id !== message.contact_id
          : userStore.user?.id !== message.user_id
      )
      .forEach((message: Message) => setUsernameForMessage(channelId, message))

    if (resetPagination) {
      messages.value = newMessages
      scrollToBottom()
    } else {
      messages.value = [...newMessages, ...messages.value]
    }
  }

  const translateMessages = async (newMessages: Message[]) => {
    for (const message of newMessages) {
      if (
        userStore.isLoggedInAsContact
          ? userStore.contact?.id !== message.contact_id
          : userStore.user?.id !== message.user_id
      ) {
        if (message.language_code !== currentLanguage.value) {
          if (!message.language_code) {
            message.language_code = 'de'
          }
          const existingTranslation = message.message_translations.find(
            (translation: MessageTranslation) => translation.language_code === currentLanguage.value
          )
          if (existingTranslation) {
            message.translatedMessage = existingTranslation.translation
          } else {
            const translatedMessage = await useApi(`${translateEndpoint.value}`, 'POST', {
              targetLanguage: currentLanguage.value,
              text: message.message
            })
            message.translatedMessage = translatedMessage
            useApi(`${chatEndpoint.value}/message-translations/${message.id}`, 'POST', {
              language_code: currentLanguage.value,
              translation: translatedMessage
            })
          }
        }
      }
    }
    return newMessages
  }

  const loadMoreMessages = async () => {
    if (!selectedChannel.value || !hasMoreMessages.value || isLoadingMoreMessages.value) return
    isLoadingMoreMessages.value = true
    currentPage.value++

    const prevScrollHeight = messagesContainer.value?.scrollHeight || 0

    await loadMessages(selectedChannel.value.id, false)

    await nextTick()
    if (messagesContainer.value && prevScrollHeight > 0) {
      const newScrollHeight = messagesContainer.value.scrollHeight
      const scrollDiff = newScrollHeight - prevScrollHeight
      messagesContainer.value.scrollTop = scrollDiff
    }

    isLoadingMoreMessages.value = false
  }

  const selectChannel = (channel: Channel) => {
    selectedChannel.value = channel

    const readUpTo = DateTime.now().toFormat('yyyy-MM-dd HH:mm:ss')
    useApi(`${chatEndpoint.value}/participant-status`, 'PUT', {
      channel_id: selectedChannel.value?.id,
      read_up_to: readUpTo,
      ...(userStore.isLoggedInAsContact
        ? { contact_id: userStore.contact.id }
        : { user_id: userStore.user.id })
    })

    if (channel.participant_status) {
      channel.participant_status.read_up_to = readUpTo
    } else {
      channel.participant_status = { read_up_to: readUpTo, received_up_to: readUpTo }
    }

    unreadMessages.value.delete(channel.id)
  }

  const clearChannel = () => {
    messages.value = []
    selectedChannel.value = null
  }

  const clearChats = () => {
    clearChannel()
    channels.value = []
    participantsByChannelId.value = new Map()
  }

  function moveChannelToTop(channelId: number) {
    const idx = channels.value.findIndex((c) => c.id === channelId)
    if (idx === -1) return
    const updated = [...channels.value]
    updated[idx] = { ...updated[idx], updated_at: new Date().toISOString() }
    if (idx !== 0) {
      const [channel] = updated.splice(idx, 1)
      updated.unshift(channel)
    }
    channels.value = updated
    bkGroupsKey.value++
  }

  const computeUnreadMessages = () => {
    const result = new Map<number, Message[]>()
    for (const channel of channels.value) {
      const readUpTo = channel.participant_status?.read_up_to
      const readUpToDate = readUpTo ? new Date(readUpTo) : null
      const unread = (channel.messages || []).filter((m) => {
        const isFromCurrentUser = userStore.isLoggedInAsContact
          ? m.contact_id === userStore.contact?.id
          : m.user_id === userStore.user?.id
        if (isFromCurrentUser) return false
        return readUpToDate ? new Date(m.created_at) > readUpToDate : true
      })
      if (unread.length) result.set(channel.id, unread)
    }
    unreadMessages.value = result
  }

  const loadChannels = async () => {
    isLoadingChannels.value = true
    const channelsResponse = await useApi(
      `${chatEndpoint.value}/channels?include=participants,messages,messages.messageTranslations,participantStatus&per_page=1000&additional_${contactOrUser.value}_id=${userStore.currentId}&sort=-updated_at`,
      'GET'
    )

    channels.value = channelsResponse.data ?? []

    if (channels.value?.length) {
      computeUnreadMessages()
      await loadInitialOrMissingParticipantsByChannel(channels.value)
      bkGroupsKey.value++
    }
    isLoadingChannels.value = false
  }

  const sendMessage = async (text: string) => {
    if (!selectedChannel.value) return

    const response = await useApi(`${chatEndpoint.value}/messages`, 'POST', {
      channel_id: selectedChannel.value.id,
      language_code: currentLanguage.value,
      message: text.trim()
    })

    messages.value.push(response.data)
    selectedChannel.value.messages?.unshift(response.data)
    moveChannelToTop(selectedChannel.value.id)
    scrollToBottom()
  }

  const findExistingChannelById = (channelId: number) => {
    return channels.value.find((channel) => channel.id === channelId)
  }

  const uniqueIds = (ids: number[] = []) => [...new Set(ids)]

  const normalizeIds = (ids: number[] = []) => uniqueIds(ids).sort((a, b) => a - b)

  const hasSameIds = (left: number[] = [], right: number[] = []) => {
    const normalizedLeft = normalizeIds(left)
    const normalizedRight = normalizeIds(right)

    if (normalizedLeft.length !== normalizedRight.length) return false

    return normalizedLeft.every((id, index) => id === normalizedRight[index])
  }

  const findExistingChannel = ({
    subject = '',
    participants = [],
    contacts = []
  }: {
    subject?: string
    participants?: number[]
    contacts?: number[]
  }) =>
    channels.value.find(
      (channel) =>
        (channel.subject || '') === subject &&
        hasSameIds(channel.participants || [], participants) &&
        hasSameIds(channel.contacts || [], contacts)
    )

  const registerChannel = async (channel: Channel) => {
    if (findExistingChannelById(channel.id)) return channel

    channel.messages = channel.messages || []
    channels.value = [channel, ...channels.value]
    await loadInitialOrMissingParticipantsByChannel([channel])

    return channel
  }

  const ensureChannel = async ({
    subject,
    isGroupChannel,
    participants,
    contacts
  }: {
    subject?: string
    isGroupChannel?: boolean
    participants: number[]
    contacts?: number[]
  }) => {
    const uniqueParticipants = uniqueIds(participants)
    const uniqueContacts = uniqueIds(contacts || [])
    const existingChannel = findExistingChannel({
      subject: subject || '',
      participants: uniqueParticipants,
      contacts: uniqueContacts
    })

    if (existingChannel) return existingChannel

    const response = await useApi(`${chatEndpoint.value}/channels`, 'POST', {
      ...(subject ? { subject } : {}),
      is_group_channel:
        typeof isGroupChannel === 'boolean'
          ? isGroupChannel
          : uniqueParticipants.length + uniqueContacts.length > 2,
      participants: uniqueParticipants,
      contacts: uniqueContacts
    })

    return registerChannel(response.data)
  }

  const createChannelWithMultipleUsers = async (
    userIds: number[],
    bkUserId: number,
    contactId?: number,
    options: { createRelatedChannels?: boolean } = {}
  ) => {
    const resolvedContactId = contactId ?? userStore.currentId
    if (!resolvedContactId) return
    const shouldCreateRelatedChannels = options.createRelatedChannels ?? true

    const directChannelPromise = ensureChannel({
      isGroupChannel: true,
      participants: [bkUserId],
      contacts: [resolvedContactId]
    })

    const relatedChannelPromises = shouldCreateRelatedChannels
      ? [
          ensureChannel({
            isGroupChannel: true,
            participants: [...userIds, ADMIN_USER_ID],
            contacts: [resolvedContactId]
          })
        ]
      : []

    const [channel] = await Promise.all([directChannelPromise, ...relatedChannelPromises])

    navigateTo(`/chat/${channel.id}`)
  }

  const createChannelWithContactAndUsers = async (
    userIds: number[],
    assignedUserIds: number[],
    contactId: number
  ) => {
    const currentUserId = userStore.user.id
    if (!currentUserId) return

    const directChannelPromise = ensureChannel({
      isGroupChannel: true,
      participants: [currentUserId],
      contacts: [contactId]
    })

    const groupChannelPromise = ensureChannel({
      isGroupChannel: true,
      participants: [currentUserId, ...userIds, ADMIN_USER_ID],
      contacts: [contactId]
    })

    const customerAdvisorChannelPromise = assignedUserIds?.length
      ? ensureChannel({
          subject: 'kundenbetreuer',
          isGroupChannel: assignedUserIds.length > 1,
          participants: [...assignedUserIds, currentUserId],
          contacts: [contactId]
        })
      : Promise.resolve(null)

    const [channel] = await Promise.all([
      directChannelPromise,
      groupChannelPromise,
      customerAdvisorChannelPromise
    ])

    navigateTo(`/chat/${channel.id}`)
  }

  const loadInitialOrMissingParticipantsByChannel = async (channels: Channel[]) => {
    if (!channels || channels?.length === 0) return

    if (participantsByChannelId.value.size) await loadMissingParticipantsByChannel(channels)
    else await loadParticipantsByChannel(channels)
  }

  const loadMissingParticipantsByChannel = async (channels: Channel[]) => {
    const missingChannels = channels.filter(
      (channel) => !participantsByChannelId.value.has(channel.id)
    )
    if (missingChannels && missingChannels?.length > 0)
      await loadParticipantsByChannel(missingChannels)
  }

  const loadParticipantsByChannel = async (channels: Channel[]) => {
    const userIds = new Set(channels.flatMap((channel) => channel.participants || []))
    const contactIds = new Set(channels.flatMap((channel) => channel.contacts || []))
    if (userStore.user?.id) userIds.delete(userStore.user?.id)
    else if (userStore.contact?.id) contactIds.delete(userStore.contact.id)

    const participantRequests = [
      useApi(`${usersEndpoint.value}?filter[id]=${Array.from(userIds).join()}&per_page=1000`, 'GET')
    ]

    const shouldIncludeContacts = contactIds.size > 0

    if (shouldIncludeContacts) {
      participantRequests.push(
        useApi(
          `${contactsEndpoint.value}?filter[id]=${Array.from(
            contactIds
          ).join()}&per_page=1000&include=custom_fields_0`,
          'GET'
        )
      )
    }

    const [usersResponse, contactsResponse] = await Promise.all(participantRequests)

    const userById = new Map((usersResponse?.data ?? []).map((user: User) => [user.id, user]))

    const contactById = new Map(
      (contactsResponse?.data ?? []).map((contact: Contact) => [contact.id, contact])
    )

    channels.forEach((channel) => {
      const users = (channel.participants || [])
        .map((id) => userById.get(id))
        .filter((user): user is User => user !== undefined)

      const contacts = (channel.contacts || [])
        .map((id) => contactById.get(id))
        .filter((contact): contact is Contact => contact !== undefined)

      participantsByChannelId.value.set(channel.id, [...users, ...contacts])
    })
  }

  const getParticipantFromMessage = (message: Message, participants: (User | Contact)[]) =>
    message.contact_id
      ? participants.find((participant) => participant.id === message.contact_id)
      : participants.find((participant) => participant.id === message.user_id)

  const getContactChatName = (contact: Contact) =>
    `${contact.custom_fields_0?.v_1 ?? ''} ${contact.custom_fields_0?.v_2 ?? ''}`.trim() ||
    `${contact.vorname ?? ''} ${contact.name ?? ''}`.trim() ||
    contact.firma ||
    contact.mail ||
    ''

  const setUsernameForMessage = (channelId: number, message: Message) => {
    const participants = participantsByChannelId.value.get(channelId)
    const currentParticipant = getParticipantFromMessage(message, participants || [])
    if (currentParticipant) {
      if (checkIsUser(currentParticipant)) {
        message.user_name = currentParticipant.name
      } else {
        message.user_name = getContactChatName(currentParticipant)
      }
    }
  }

  const sortedChannels = computed(() =>
    [...channels.value].toSorted((a, b) => {
      const aHasMessages = (a.messages?.length ?? 0) > 0
      const bHasMessages = (b.messages?.length ?? 0) > 0
      if (aHasMessages !== bHasMessages) return aHasMessages ? -1 : 1
      return b.updated_at > a.updated_at ? 1 : -1
    })
  )

  const groupedChannels = computed(() => {
    if (!userStore.isLoggedInAsContact) return null

    const bkDirectChats = channels.value.filter(
      (c) =>
        c.subject !== 'kundenbetreuer' &&
        (c.participants?.length ?? 0) === 1 &&
        ((participantsByChannelId.value.get(c.id) || []).find(checkIsUser) as User | undefined)
          ?.role_id === CARE_NURSE_ROLE_ID
    )

    if (!bkDirectChats.length) return null

    return bkDirectChats
      .map((bkChat, index) => {
        const bkId = bkChat.participants[0]
        const bkUser = (participantsByChannelId.value.get(bkChat.id) || []).find(checkIsUser) as
          User | undefined
        const groupChat = channels.value.find(
          (c) =>
            c.subject !== 'kundenbetreuer' &&
            (c.participants?.length ?? 0) > 1 &&
            c.participants?.includes(bkId)
        )
        const groupChannels: Channel[] = [...(groupChat ? [groupChat] : []), bkChat].toSorted(
          (a, b) => (b.updated_at > a.updated_at ? 1 : -1)
        )
        const latestUpdated = groupChannels[0]?.updated_at ?? ''
        return {
          bkName: bkUser?.name ?? `Rotation ${index + 1}`,
          channels: groupChannels,
          latestUpdated
        }
      })
      .toSorted((a, b) => b.latestUpdated.localeCompare(a.latestUpdated))
  })

  const getUserIdsForChat = async (
    userId?: number,
    isPatient: boolean = false,
    includeUserId: boolean = false
  ) => {
    const contactUserRelation = (
      await useApi(`css/contact-user-relation?filter[user_id]=${userId}`, 'GET')
    ).data

    const knr = contactUserRelation?.[0]?.knr

    // css/contacts resolves a related contact (caregiver <-> patient) through the
    // signed-in contact's own token now that LimitContactsScope follows the
    // contact relation, so the counterpart loads in both directions without the
    // old service-token proxy.
    const contact = knr
      ? (await useApi(`css/contacts/${knr}?include=assignedUsers,contactGroups`, 'GET'))?.data
      : null

    const assignedUserIds = (contact?.assigned_users ?? []).map(
      (assignedUser: { user_id: number }) => assignedUser.user_id
    )

    const baseUserIds =
      includeUserId && userId ? [userId, ...assignedUserIds] : [...assignedUserIds]

    const contactGroupUser = contact?.contact_groups?.[0]?.description
    if (contactGroupUser?.length) baseUserIds.push(+contactGroupUser)

    return { userIds: baseUserIds, assignedUserIds }
  }

  const createChatForContact = async (
    userId: number,
    contactId?: number,
    options?: { createRelatedChannels?: boolean }
  ) => {
    const { userIds } = await getUserIdsForChat(userId, true, true)

    await createChannelWithMultipleUsers(userIds, userId, contactId, options)
  }

  const createChatForUser = async (contactId: number) => {
    const { userIds, assignedUserIds } = await getUserIdsForChat(userStore.user.id, false, false)

    await createChannelWithContactAndUsers(userIds, assignedUserIds, contactId)
  }

  return {
    channels,
    sortedChannels,
    participantsByChannelId,
    messages,
    selectedChannel,
    messagesContainer,
    unreadMessages,
    isLoadingChannels,
    chatEndpoint,
    translateEndpoint,
    checkIsUser,
    contactOrUser,
    currentLanguage,
    hasMoreMessages,
    isLoadingMoreMessages,
    groupedChannels,
    bkGroupsKey,
    findExistingChannelById,
    loadMessages,
    loadMoreMessages,
    loadChannels,
    selectChannel,
    moveChannelToTop,
    clearChannel,
    sendMessage,
    scrollToBottom,
    setUsernameForMessage,
    loadInitialOrMissingParticipantsByChannel,
    clearChats,
    createChatForUser,
    createChatForContact,
    translateMessages
  }
})
